import { useState } from "react";
import { Link } from "wouter";
import { useMe } from "../api/use-me.js";
import { ProfileCard } from "../components/profile-card.js";
import { SectionHeader } from "../components/help-button.js";
import { HELP } from "../components/help-content.js";
import { MachinesSection } from "../components/machines-section.js";
import { MyCredentialsSection } from "../components/my-credentials-section.js";
import { PasswordChangeModal } from "../components/password-change-modal.js";

// 2.5.D.2 (vaihe 2.5): /asetukset-sivu. Read-only v1 — profiili-card,
// projekti-jäsenyydet ja age-public-key copy:ttavissa.

export function Asetukset() {
  const { me, isLoading, error } = useMe();
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangedAt, setPasswordChangedAt] = useState<string | null>(null);

  if (isLoading) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-10">
        <h2 className="text-2xl font-semibold text-slate-100">Asetukset</h2>
        <p className="mt-2 text-slate-400">Ladataan…</p>
      </section>
    );
  }

  if (error || !me) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-10">
        <h2 className="text-2xl font-semibold text-slate-100">Asetukset</h2>
        <p className="mt-2 text-red-400">
          Profiilin lataus epäonnistui{error ? `: ${error.message}` : ""}.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6 px-6 py-10">
      <h2 className="text-2xl font-semibold tracking-tight text-slate-100">Asetukset</h2>

      <ProfileCard user={me.user} />

      <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
        <SectionHeader title="Projektit" help={HELP.projects!.body} />
        {me.user.projects.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">
            Ei vielä projekteja. Luo projekti{" "}
            <Link href="/projektit" className="text-cyan-400 hover:underline">
              Projektit-näkymässä
            </Link>
            .
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-700">
            {me.user.projects.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between py-3 text-sm"
              >
                <Link
                  href={`/projektit/${p.id}`}
                  className="font-medium text-cyan-400 hover:underline"
                >
                  {p.name}
                </Link>
                <span className="rounded bg-slate-800 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-300">
                  {p.access}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <MyCredentialsSection />

      <MachinesSection />

      <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
        <SectionHeader title="Tilin hallinta" help={HELP.account!.body} />
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-100">Salasana</h4>
              <p className="mt-1 text-xs text-slate-500">
                Vaihtaminen lopettaa kaikki muut kirjautuneet sessiot.
              </p>
              {passwordChangedAt && (
                <p className="mt-1 text-xs text-emerald-300">
                  ✓ Salasana vaihdettu {passwordChangedAt}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowPasswordChange(true)}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
            >
              Vaihda salasana
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Sähköpostin vaihto ja age-avaimen rotation tulevat myöhemmässä
            vaiheessa — ota yhteyttä ylläpitäjään jos tarvitset näitä nyt.
          </p>
        </div>
      </section>

      {showPasswordChange && (
        <PasswordChangeModal
          onClose={() => setShowPasswordChange(false)}
          onSuccess={() => {
            setShowPasswordChange(false);
            setPasswordChangedAt(new Date().toLocaleString("fi-FI"));
          }}
        />
      )}
    </section>
  );
}
